import Map from "ol/Map.js";
import View from "ol/View.js";
import { attachRasterLoadTracking, attachTileLoadTracking } from "@/services/layers/rasterProgressSource.js";
import TileLayer from "ol/layer/Tile.js";
import OSM from "ol/source/OSM.js";
import XYZ from "ol/source/XYZ.js";
import { fromLonLat, transformExtent } from "ol/proj.js";
import { defaults as defaultControls } from "ol/control.js";
import VectorTileLayer from "ol/layer/VectorTile.js";
import VectorTileSource from "ol/source/VectorTile.js";
import MVT from "ol/format/MVT.js";
import { stylefunction } from "ol-mapbox-style";
import TileWMS from 'ol/source/TileWMS.js';
import ImageArcGISRest from 'ol/source/ImageArcGISRest.js';
import ImageLayer from 'ol/layer/Image.js';
import store from '@/store.js';
import { getConnector } from '@/services/externalLayers';
import { createStyleFunction, dynamicLayersParam } from '@/services/layers/symbology.js';

// Encuadre de las 16 regiones de Chile: extent en EPSG:4326 [minLon, minLat, maxLon, maxLat]
// + zoom predefinido (usado en vez del zoom auto-calculado por fit(), para un encuadre consistente
// sin importar el tamaño de la ventana). Extents calculados desde public/data/chileRegiones2018.geojson
// (campo NOM_REGION). Valparaíso y Magallanes se recortan al territorio continental (excluyen Isla de
// Pascua y el territorio antártico chileno respectivamente); esos territorios insulares siguen
// accesibles navegando manualmente el mapa.
// El zoom es una estimación inicial (ajustar el valor acá si no se ve bien en pantalla).
export const REGION_VIEWS_4326 = {
  'Arica y Parinacota': { extent: [-70.3805, -19.23, -68.9116, -17.4978], zoom: 8.5 },
  'Tarapacá': { extent: [-70.2866, -21.6306, -68.4045, -18.9369], zoom: 8.5 },
  'Antofagasta': { extent: [-70.743, -26.061, -66.9909, -20.9346], zoom: 8.5 },
  'Atacama': { extent: [-71.5913, -29.5351, -68.2628, -25.2858], zoom: 8.5 },
  'Coquimbo': { extent: [-71.7173, -32.2825, -69.8092, -29.0374], zoom: 8.5 },
  'Valparaíso': { extent: [-71.839, -33.956, -69.989, -32.021], zoom: 8.5 },
  'Metropolitana de Santiago': { extent: [-71.7152, -34.2909, -69.77, -32.9219], zoom: 8.5 },
  "Libertador General Bernardo O'Higgins": { extent: [-72.0573, -35.0052, -70.0092, -33.8508], zoom: 8.5 },
  'Maule': { extent: [-72.7859, -36.5437, -70.3133, -34.6846], zoom: 8.5 },
  'Ñuble': { extent: [-72.8861, -37.1978, -71.007, -36.0054], zoom: 8.5 },
  'Biobío': { extent: [-73.9613, -38.4926, -70.9833, -36.4426], zoom: 8.5 },
  'La Araucanía': { extent: [-73.5195, -39.6372, -70.8266, -37.5817], zoom: 8.5 },
  'Los Ríos': { extent: [-73.7254, -40.6811, -71.5933, -39.2869], zoom: 8.5 },
  'Los Lagos': { extent: [-74.8377, -44.0671, -71.5809, -40.2388], zoom: 8.5 },
  'Aysén del General Carlos Ibáñez del Campo': { extent: [-75.6779, -49.1583, -71.0899, -43.6383], zoom: 8.5 },
  'Magallanes y de la Antártica Chilena': { extent: [-75.723, -55.983, -66.416, -48.594], zoom: 8.5 }
};

class MapService {
  constructor() {
    this.map = null;
    this.baseLayer = null;
    this._baseOverlay = null;

    // Configuración inicial del mapa
    this.initialCenter = fromLonLat([-70.4, -33.27]);
    this.initialZoom = 3.5;

    this.baseLayersCatalog = {
      osm: () => new OSM(),
      satellite: () =>
        new XYZ({
          url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
        }),
      terrain: () =>
        new XYZ({
          url: "https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}",
        }),
      dark: () =>
        new XYZ({
          url: "https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
        }),
    };
  }

  /**
   * Inicializa el mapa si no existe ya.
   */
  init(target = "map") {
    if (this.map) return this.map;

    const container = document.getElementById(target);
    if (!container) {
      console.error(`[MapService] No se encontró el contenedor #${target}`);
      return null;
    }

    // Crear capa base inicial
    this.baseLayer = new TileLayer({
      source: new OSM(),
    });

    this.map = new Map({
      target,
      layers: [this.baseLayer],
      view: new View({
        center: this.initialCenter,
        zoom: this.initialZoom,
      }),
      controls: defaultControls({
        zoom: false,
        rotate: false,
        attribution: true,
      }),
    });

    return this.map;
  }

  /**
   * Retorna el mapa actual
   */
  get() {
    return this.map;
  }

  /**
   * Cambia la capa base dinámicamente.
   * On tile/style load failure: alert + rollback to previous base layer.
   */
  changeBaseMap(modeOrConfig) {
    if (!this.map) return;

    const previousLayer = this.baseLayer;
    const previousOverlay = this._baseOverlay;
    let newLayer = null;

    try {
      if (typeof modeOrConfig === 'string') {
        const sourceFactory = this.baseLayersCatalog[modeOrConfig];
        if (!sourceFactory) {
          console.warn(`[MapService] Basemap ID "${modeOrConfig}" no encontrado en catálogo.`);
          return;
        }
        newLayer = new TileLayer({ source: sourceFactory(), zIndex: 0 });
      } else if (modeOrConfig && typeof modeOrConfig === 'object') {
        newLayer = this._buildBaseLayerFromConfig(modeOrConfig, previousLayer);
      }
    } catch (err) {
      console.error('[MapService] Error construyendo capa base:', err);
      alert('Error cargando mapa base. Manteniendo mapa anterior.');
      return;
    }

    if (!newLayer) {
      console.warn('[MapService] No se pudo crear la capa para configuración:', modeOrConfig);
      return;
    }

    if (newLayer instanceof TileLayer) {
      this._attachTileErrorRollback(newLayer, previousLayer, previousOverlay);
    }

    this._swapBaseLayer(newLayer);
    this._applyOverlay(modeOrConfig);
    console.log('[MapService] Capa base cambiada correctamente.');
  }

  /**
   * Aplica una capa base ya construida (GeoPackage offline, PB-121).
   * El llamador garantiza que la capa cargó/parseó con éxito antes de invocar.
   */
  applyBaseLayerDirect(layer) {
    if (!this.map) return;
    this._swapBaseLayer(layer);
    this._applyOverlay(null); // remueve overlay del basemap anterior
  }

  _buildBaseLayerFromConfig(config, previousLayer) {
    const isVector = config.type === 'vector-tile' || config.url?.includes('VectorTileServer');
    if (isVector) return this._buildVectorTileLayer(config, previousLayer);
    if (config.type === 'osm' || config.provider === 'OSM') {
      return new TileLayer({ source: new OSM(), zIndex: 0 });
    }
    if (config.url) {
      let url = config.url;
      if (!url.includes('{x}') && !url.includes('{z}')) {
        url = `${url}/tile/{z}/{y}/{x}`;
      }
      console.log(`[MapService] Aplicando basemap XYZ: ${url}`);
      return new TileLayer({
        source: new XYZ({ url, attributions: config.attributions }),
        zIndex: 0,
      });
    }
    return null;
  }

  _buildVectorTileLayer(config, previousLayer) {
    console.log(`[MapService] Aplicando basemap vectorial: ${config.styleUrl || config.url}`);
    let url = config.url || '';
    if (url.includes('VectorTileServer') && !url.includes('{z}')) {
      url = `${url}/tile/{z}/{y}/{x}.pbf`;
    }
    const source = new VectorTileSource({
      format: new MVT(),
      url,
      attributions: config.attributions || '© Esri, OpenStreetMap contributors',
    });
    const layer = new VectorTileLayer({ source, zIndex: 0 });

    const styleUrl = config.styleUrl || (url.includes('arcgis')
      ? 'https://cdn.arcgis.com/sharing/rest/content/items/4a7116253de34cd59511c8abc2cc3ac5/resources/styles/root.json'
      : null);

    if (styleUrl) {
      fetch(styleUrl)
        .then((r) => {
          if (!r.ok) throw new Error(`Style fetch ${r.status}`);
          return r.json();
        })
        .then((glStyle) => {
          const sourceKey = glStyle.sources ? Object.keys(glStyle.sources)[0] : 'esri';
          stylefunction(layer, glStyle, sourceKey);
        })
        .catch((err) => {
          console.error('[MapService] Error cargando estilo vectorial:', err);
          alert('Error cargando estilo de mapa base. Volviendo al mapa anterior.');
          if (previousLayer && this.baseLayer === layer) this._swapBaseLayer(previousLayer);
        });
    }
    return layer;
  }

  _swapBaseLayer(newLayer) {
    if (this.baseLayer && this.baseLayer !== newLayer) {
      this.map.removeLayer(this.baseLayer);
    }
    this.baseLayer = newLayer;
    this.map.getLayers().insertAt(0, newLayer);
  }

  _attachTileErrorRollback(newLayer, previousLayer, previousOverlay) {
    const source = newLayer.getSource();
    if (!source || typeof source.on !== 'function') return;
    let handled = false;
    source.on('tileloaderror', () => {
      if (handled) return;
      handled = true;
      console.error('[MapService] tileloaderror en capa base, rollback');
      alert('Error cargando mapa base. Volviendo al mapa anterior.');
      if (previousLayer && this.baseLayer === newLayer) {
        this._swapBaseLayer(previousLayer);
        this._restoreOverlay(previousOverlay);
      }
    });
  }

  _applyOverlay(config) {
    if (this._baseOverlay) {
      this.map.removeLayer(this._baseOverlay);
      this._baseOverlay = null;
    }
    if (config && typeof config === 'object' && config.overlayUrl) {
      const overlay = new TileLayer({
        source: new XYZ({ url: config.overlayUrl }),
        zIndex: 5,
      });
      this.map.addLayer(overlay);
      this._baseOverlay = overlay;
    }
  }

  _restoreOverlay(previousOverlay) {
    if (this._baseOverlay) {
      this.map.removeLayer(this._baseOverlay);
      this._baseOverlay = null;
    }
    if (previousOverlay) {
      this.map.addLayer(previousOverlay);
      this._baseOverlay = previousOverlay;
    }
  }

  /**
   * Retorna la capa base actual
   */
  getBaseLayer() {
    return this.baseLayer;
  }

  destroy() {
    if (this.map) {
      this.map.setTarget(null)
      this.map = null
    }

    this.baseLayer = null
  }

  getActiveLayers() {
    return this.map.getLayers().getArray();
  }

  layerExists(layerId) {
    if (!this.map) return false;
    const layers = this.map.getLayers().getArray();
    const exists = layers.some(layer => layer.get("id") === layerId);
    return exists;
  }


  addLayerToMap(layer, filterGeometry = null) {
    if (!this.map) return;
    if (this.layerExists(layer.get("id"))) return;

    if (filterGeometry) {
      this.applyLayerClipping(layer, filterGeometry);
    }

    this.map.addLayer(layer);

    // Único punto por donde una capa OL entra al mapa: reaplicar acá cubre
    // encender una capa, encender la categoría completa y "Zoom a capa" sin
    // parchear los tres llamadores de LayerService.addLayerToMap (que es async
    // vía addLayerArcgis y no retorna su promesa: parchear el llamador aplicaría
    // la simbología antes de que exista la capa OL).
    const renderer = store.getters['symbology/activeRenderer'](layer.get('id'));
    if (renderer) this.applyLayerSymbology(layer.get('id'), renderer);
  }

  applyLayerClipping(layer, filterGeometry) {
    const geometryType = filterGeometry.type;
    const coordinates = filterGeometry.coordinates;

    // Al exportar se reemplazan temporalmente capas raster remotas por una
    // imagen obtenida vía backend. Guardar la geometría permite aplicar el
    // mismo recorte al reemplazo sin alterar el renderizado habitual.
    layer.set('printClipGeometry', filterGeometry);

    layer.on('prerender', (e) => {
      const ctx = e.context;
      const frameState = e.frameState;
      const pixelRatio = frameState.pixelRatio;

      ctx.save();
      ctx.beginPath();

      const drawRing = (ring) => {
        ring.forEach((coord, i) => {
          const pixel = this.map.getPixelFromCoordinate(coord);
          // Handle cases where pixel might be null (though getPixelFromCoordinate usually returns valid [x,y])
          if (!pixel) return;

          const x = pixel[0] * pixelRatio;
          const y = pixel[1] * pixelRatio;

          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.closePath();
      };

      try {
        if (geometryType === 'Polygon') {
          // GeoJSON Polygon coordinates: [outerRing, hole1, hole2...]
          // OpenLayers simple geometry usually expects standard array structure
          // We iterate over rings
          coordinates.forEach(ring => drawRing(ring));
        } else if (geometryType === 'MultiPolygon') {
          // MultiPolygon: [ [rings], [rings] ]
          coordinates.forEach(polygon => {
            polygon.forEach(ring => drawRing(ring));
          });
        }
      } catch (err) {
        console.error("Error applying clip", err);
      }

      ctx.clip();
    });

    layer.on('postrender', (e) => {
      const ctx = e.context;
      ctx.restore();
    });
  }

  removeLayerFromMapByID(layerID) {
    if (!this.map) return;
    console.log("Removiendo capa por ID:", layerID);

    const layersToRemove = [];
    this.map.getLayers().forEach(layer => {
      if (layer && layer.get("id") === layerID) {
        layersToRemove.push(layer);
      }
    });

    layersToRemove.forEach(layer => {
      layer.getSource()?.get('abortController')?.abort();
      layer.get('detachLoadTracking')?.();
      this.map.removeLayer(layer);
    });
  }

  /**
   * Obtiene el extent (bounding box) actual del mapa
   * Returns: [xMin, yMin, xMax, yMax] en EPSG:3857
   */
  getExtent() {
    if (!this.map) return null;
    return this.map.getView().calculateExtent(this.map.getSize());
  }

  /**
   * Hace zoom a un extent específico
   */
  zoomToExtent(extent) {
    if (!this.map) return;

    this.map.getView().fit(extent, {
      duration: 1000,
    });
  }

  /**
   * Hace zoom a un extent en EPSG:4326 (ej. bbox extraído de un GetCapabilities WMS/WFS),
   * transformándolo a la proyección del mapa antes de aplicar el fit.
   */
  zoomToLonLatExtent(extent4326) {
    if (!this.map) return;

    const extent = transformExtent(extent4326, "EPSG:4326", this.map.getView().getProjection());
    this.zoomToExtent(extent);
  }

  /**
   * Aplica el encuadre asignado al usuario. "Nacional" usa la vista país;
   * las regiones territoriales usan su centro y zoom predefinidos.
   * @param {String} regionName "Nacional" o un nombre presente en REGION_VIEWS_4326.
   * @param {Object} options { duration } — duración de la animación en ms (0 = sin animación).
   * @returns {Boolean} TRUE si la región existe y se aplicó el zoom.
   */
  zoomToRegionExtent(regionName, options = {}) {
    if (!this.map) return false;

    if (regionName === 'Nacional') {
      this.zoomToNational(options);
      return true;
    }

    const view = REGION_VIEWS_4326[regionName];
    if (!view) return false;

    const [minLon, minLat, maxLon, maxLat] = view.extent;
    const center = fromLonLat([(minLon + maxLon) / 2, (minLat + maxLat) / 2]);

    this.map.getView().animate({
      center,
      zoom: view.zoom,
      duration: options.duration !== undefined ? options.duration : 1000,
    });
    return true;
  }

  /**
   * Vuelve al encuadre nacional por defecto.
   * @param {Object} options { duration } — duración de la animación en ms (0 = sin animación).
   */
  zoomToNational(options = {}) {
    if (!this.map) return;

    this.map.getView().animate({
      center: this.initialCenter,
      zoom: this.initialZoom,
      duration: options.duration !== undefined ? options.duration : 1000,
    });
  }

  /**
   * Añade una capa operativa (WMS o ArcGIS) al mapa
   * @param {Object} layerConfig Configuración de la capa desde el backend/store
   */
  /**
   * Muestra un toast una sola vez si un TileWMS falla en cargar tiles (servidor caído,
   * parámetros rechazados, etc.). "tileloaderror" dispara por cada tile fallido — sin el
   * guard "warned", paneando/haciendo zoom sobre una capa caída spamearía el toast sin parar.
   */
  _attachTileErrorWarning(source, layerName) {
    let warned = false;
    source.on('tileloaderror', () => {
      if (warned) return;
      warned = true;
      store.dispatch('toast/show', {
        message: `No se pudieron cargar los tiles de "${layerName}" — verifica que el servicio WMS esté disponible.`,
        type: 'error'
      });
    });
  }

  addLayer(layerConfig) {
    if (!this.map) return;
    if (this.layerExists(layerConfig.id)) {
      console.log(`[MapService] La capa ${layerConfig.name} ya existe.`);
      return;
    }

    console.log(`[MapService] Añadiendo capa:`, layerConfig);
    let olLayer = null;

    try {
      const externalConnector = getConnector(layerConfig.provider.name);

      if (layerConfig.provider.name === "geoserver") {
        // Configuración WMS (GeoServer)
        const geoserverData = layerConfig.provider.geoserverdata;
        // Usar filename o datastore como nombre de capa WMS
        const layerName = geoserverData.filename || geoserverData.datastore;
        const layersParam = `${geoserverData.workspace}:${layerName}`;

        const source = new TileWMS({
          url: layerConfig.provider.url,
          params: {
            'LAYERS': layersParam,
            'TILED': true,
            'VERSION': '1.1.1',
          },
          serverType: 'geoserver',
          crossOrigin: 'anonymous',
        });
        this._attachTileErrorWarning(source, layerConfig.name);

        olLayer = new TileLayer({
          source: source,
          opacity: layerConfig.opacity !== undefined ? layerConfig.opacity : 1,
          zIndex: 10 // Capas operativas sobre el mapa base
        });
        olLayer.set('detachLoadTracking', attachTileLoadTracking(source, { layerId: layerConfig.id, layerName: layerConfig.name }));

      } else if (externalConnector) {
        // Capa externa "de sesión" (no persistida): WMS/WFS/Esri REST agregada por URL desde
        // la herramienta "Subir WMS/WFS". El conector concreto vive en services/externalLayers/.
        olLayer = externalConnector.createOlLayer(layerConfig);

      } else if (layerConfig.provider.name === "arcgis") {
        // Configuración ArcGIS
        // Nota: Soportar FeatureServer vs MapServer si es necesario.
        // Por ahora asumimos MapServer (ImageArcGISRest) para simplicidad y compatibilidad con visor clásico
        // Si tiene FeatureServer, se manejaría diferente (VectorLayer), pero empezamos con esto.

        const source = new ImageArcGISRest({
          ratio: 1,
          params: {},
          url: layerConfig.provider.url
        });

        olLayer = new ImageLayer({
          source: source,
          opacity: layerConfig.opacity !== undefined ? layerConfig.opacity : 1,
          zIndex: 10
        });
        olLayer.set('layerType', 'raster');
        olLayer.set('detachLoadTracking', attachRasterLoadTracking(source, { layerId: layerConfig.id, layerName: layerConfig.name }));
      }

      if (olLayer) {
        // Asignar ID y metadatos a la capa OL para poder buscarla después
        olLayer.set('id', layerConfig.id);
        olLayer.set('name', layerConfig.name);

        this.map.addLayer(olLayer);
        console.log(`[MapService] Capa añadida exitosamente.`);
      }

    } catch (error) {
      console.error(`[MapService] Error añadiendo capa ${layerConfig.name}:`, error);
    }
  }

  /**
   * Remueve una capa del mapa por su ID
   * @param {String|Number} layerId 
   */
  removeLayer(layerId) {
    if (!this.map) return;

    // Buscar la capa en el arreglo de capas del mapa
    const layersToRemove = [];
    this.map.getLayers().forEach(layer => {
      if (layer.get('id') === layerId) {
        layersToRemove.push(layer);
      }
    });

    layersToRemove.forEach(layer => {
      // Cancela una carga en curso (capas externas WFS/Esri REST) para no dejarla corriendo
      // en segundo plano sobre una fuente ya huérfana. No-op para capas sin AbortController
      // (GeoServer/ArcGIS).
      layer.getSource()?.get('abortController')?.abort();
      // Suelta el seguimiento de carga de las ráster: sin esto, quitar una capa mientras
      // pide su imagen dejaba el aviso de carga encendido para siempre.
      layer.get('detachLoadTracking')?.();
      this.map.removeLayer(layer);
      console.log(`[MapService] Capa removida: ${layerId}`);
    });
  }

  /**
   * Ajusta la opacidad de una capa
   * @param {String|Number} layerId 
   * @param {Number} opacity (0 a 1)
   */
  setLayerOpacity(layerId, opacity) {
    if (!this.map) return;

    let layerFound = false;
    this.map.getLayers().forEach(layer => {
      if (layer.get('id') === layerId) {
        layer.setOpacity(opacity);
        layerFound = true;
      }
    });

    if (!layerFound) {
      console.warn(`[MapService] No se encontró la capa ${layerId} para cambiar opacidad.`);
    }
  }

  /**
   * Muestra u oculta una capa sin destruirla (a diferencia de removeLayer/addLayer, conserva
   * la fuente y los features ya cargados — evita volver a pedirlos al servidor externo).
   * @param {String|Number} layerId
   * @param {Boolean} visible
   */
  setLayerVisibility(layerId, visible) {
    if (!this.map) return false;

    let layerFound = false;
    this.map.getLayers().forEach(layer => {
      if (layer.get('id') === layerId) {
        layer.setVisible(visible);
        layerFound = true;
      }
    });

    if (!layerFound) {
      console.warn(`[MapService] No se encontró la capa ${layerId} para cambiar visibilidad.`);
    }
    return layerFound;
  }

  /**
   * Busca la capa OL correspondiente a un id de capa (usado para hit-testing de capas vectoriales)
   * @param {String|Number} layerId
   * @returns {import('ol/layer/Base').default|null}
   */
  getOlLayerById(layerId) {
    if (!this.map) return null;

    let found = null;
    this.map.getLayers().forEach(layer => {
      if (layer.get('id') === layerId) {
        found = layer;
      }
    });
    return found;
  }

  // Cláusula vigente de una capa MapServer, leída del LAYERDEFS que dejó
  // updateLayerFilter. LAYERDEFS es {"<idSubcapa>": "<where>"}; se prefiere la
  // entrada del `codigo` de la capa y se cae a la única disponible cuando el id
  // del servicio no coincide con el código guardado (QA-176).
  activeDefinitionExpression(olLayer) {
    const source = olLayer.getSource();
    if (!source || typeof source.getParams !== 'function') return null;
    const raw = source.getParams().LAYERDEFS;
    if (!raw) return null;
    try {
      const defs = JSON.parse(raw);
      const own = defs[String(Number(olLayer.get('codigo')) || 0)];
      if (own) return own;
      const values = Object.values(defs).filter(Boolean);
      return values.length === 1 ? values[0] : null;
    } catch (e) {
      console.warn('[MapService] LAYERDEFS ilegible:', e);
      return null;
    }
  }

  /**
   * Applies filter params to the matching OL layer's WMS/ArcGIS source.
   * Follows the same iteration pattern as setLayerOpacity().
   * Raster layers are explicitly skipped (excluded from Phase 3 scope).
   * TODO: add raster CQL_FILTER support in a future phase.
   *
   * @param {Number|String} layerId
   * @param {Object} params  { cqlFilter?: String|null, layerDefs?: String|null, spatialFilter?: String|null }
   */
  updateLayerFilter(layerId, params = {}) {
    if (!this.map) return;
    const { cqlFilter, layerDefs, spatialFilter, featureWhere } = params;
    let found = false;

    this.map.getLayers().forEach(olLayer => {
      if (olLayer.get('id') !== layerId) return;

      if (olLayer.get('layerType') === 'raster') {
        // TODO: raster CQL support — excluded from Phase 3 scope
        return;
      }

      const source = olLayer.getSource();
      if (!source) return;

      if (featureWhere !== undefined && typeof source.updateParams !== 'function') {
        source.set('attributeFilterWhere', featureWhere || '1=1');
        source.set('attributeFilterRevision', (source.get('attributeFilterRevision') || 0) + 1);
        source.clear(true);
        source.refresh();
        found = true;
        return;
      }
      if (typeof source.updateParams !== 'function') return;

      if (cqlFilter !== undefined) {
        source.updateParams({ CQL_FILTER: cqlFilter });
      }
      if (layerDefs !== undefined) {
        source.updateParams({ LAYERDEFS: layerDefs });
        // Con una simbología personalizada activa, /export ignora LAYERDEFS: hay
        // que rehacer el dynamicLayers con la cláusula nueva o el filtro no
        // llega a la imagen del mapa (QA-176).
        const renderer = olLayer.get('activeRenderer');
        if (renderer) {
          source.updateParams({
            dynamicLayers: dynamicLayersParam(olLayer.get('codigo'), renderer, this.activeDefinitionExpression(olLayer)),
          });
        }
      }
      if (spatialFilter !== undefined) {
        source.updateParams({ spatialFilter });
      }
      found = true;
    });

    if (!found) {
      console.warn(`[MapService] updateLayerFilter: layer ${layerId} not found or skipped.`);
    }
  }

  /**
   * Aplica una simbología a la capa OL de una capa ArcGIS sin recrearla.
   * renderer null = "Predeterminada".
   * @param {Number|String} layerId
   * @param {Object|null} renderer  drawingInfo.renderer de Esri
   */
  applyLayerSymbology(layerId, renderer) {
    const olLayer = this.getOlLayerById(layerId);
    if (!olLayer) {
      console.warn(`[MapService] applyLayerSymbology: layer ${layerId} not found.`);
      return;
    }
    const source = olLayer.getSource();
    if (!source) return;

    if (typeof source.updateParams === 'function') {
      // MapServer (TileArcGISRest): re-renderiza el servidor, resultado privado
      // por usuario. `undefined` borra el parámetro de la URL de tesela
      // (ol/uri appendParams salta null/undefined) y updateParams invalida la
      // caché de teselas: la vuelta a "Predeterminada" es real, no acumulativa.
      // updateParams hace merge, así que LAYERS sobrevive. LAYERDEFS también
      // sobrevive en la URL, pero /export lo IGNORA mientras haya dynamicLayers:
      // por eso el filtro se reenvía como definitionExpression acá dentro
      // (QA-176).
      olLayer.set('activeRenderer', renderer || undefined);
      source.updateParams({
        dynamicLayers: renderer
          ? dynamicLayersParam(olLayer.get('codigo'), renderer, this.activeDefinitionExpression(olLayer))
          : undefined,
      });
      return;
    }

    // FeatureServer (VectorSource): estilo en cliente. Volver a "Predeterminada"
    // restaura el estilo con que layers.js creó la capa —el renderer propio de
    // ArcGIS—, no el default de OL. La guarda comprueba PRESENCIA, no verdad: con
    // `if (!olLayer.get(...))`, un estilo de creación falsy no se guardaría y la
    // segunda llamada guardaría la simbología personalizada.
    if (olLayer.get('defaultStyle') === undefined) {
      olLayer.set('defaultStyle', olLayer.getStyle());
    }
    olLayer.setStyle(renderer ? createStyleFunction(renderer) : olLayer.get('defaultStyle'));
  }

  /**
   * Returns the current filter params active on an OL layer's source.
   * Used by selectService to build CQL-aware GetFeatureInfo requests.
   *
   * @param {Number|String} layerId
   * @returns {{ cqlFilter: String|null, layerDefs: String|null } | null}
   */
  getLayerActiveFilter(layerId) {
    if (!this.map) return null;
    let result = null;

    this.map.getLayers().forEach(olLayer => {
      if (olLayer.get('id') !== layerId) return;
      const source = olLayer.getSource();
      if (!source) return;
      if (typeof source.getParams !== 'function') {
        result = { cqlFilter: null, layerDefs: null, spatialFilter: null, featureWhere: source.get('attributeFilterWhere') || null };
        return;
      }
      const p = source.getParams();
      result = {
        cqlFilter: p.CQL_FILTER || null,
        layerDefs:  p.LAYERDEFS  || null,
        spatialFilter: p.spatialFilter || p.SPATIALFILTER || null,
      };
    });

    return result;
  }

}

export const mapService = new MapService();
